import { detailsBase } from './billing/serviceDetailsConcurrency';
import { useCallback, useState, type ComponentProps, type Dispatch, type SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';

/** Work order states where finished work can no longer be reopened; mirrors the API (#3403). */
const REOPEN_LOCKED_STATUSES = new Set(['ready_to_invoice', 'invoiced', 'completed', 'cancelled', 'quote_rejected']);
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { UserAvatar, ExpandableCard } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { statusColors } from '@/styles/theme';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { distinctServiceTitle } from './serviceTitle';
import { isFixedPricePackage } from './fixedPricePackage';
import { useUpdateBooking } from '@/hooks/useBookings';
import { usePersistedState } from '@/hooks/usePersistedState';
import { EDIT_BILLABLE_HOURS_PERMISSION } from '@/lib/billableHours';
import { useAuthStore } from '@/stores/authStore';
import { sumHours } from '@/utils/duration';
import {
  displayedUnassignedActualHours,
  serviceBillableHours,
} from '@/utils/actualTime';
import { laborCentsFromHours } from '@/utils/money';
import { allocatedSlotHours } from '@/utils/schedule-slot';
import { DEFAULT_VAT_PERCENT, netToGrossCents } from '@/utils/vat';
import { WorkOrderDurationTrio } from './WorkOrderDurationTrio';
import { AddServiceRow } from './AddServiceRow';
import { ServiceDetailContent } from './ServiceDetailContent';
import { SortableServiceCard } from './SortableServiceCard';

type DndSensors = ComponentProps<typeof DndContext>['sensors'];

interface WorkOrderServicesSectionProps {
  workOrder: WorkOrderDetail;
  workOrderId: string;
  t: TFunction;
  expandedServiceIds: Set<string>;
  setExpandedServiceIds: Dispatch<SetStateAction<Set<string>>>;
  /** Opening one service closes the others (#3364). */
  accordion: boolean;
  activeDragId: string | null;
  dndSensors: DndSensors;
  onDragStart: (event: { active: { id: string | number } }) => void;
  onReorderServices: (event: DragEndEvent) => void | Promise<void>;
  onAddService: (serviceId: string, mechanicId?: string | null, laborItemIds?: string[] | null) => void | Promise<void>;
  onRemoveService: (bookingServiceId: string) => void | Promise<void>;
  onServiceMechanicChange: (bookingServiceId: string, mechanicId: string | null) => void | Promise<void>;
  onServiceAction: (action: string, bookingServiceId: string) => void | Promise<void>;
  setServiceDirtyStates: Dispatch<SetStateAction<Record<string, boolean>>>;
  updatePending: boolean;
  formatPrice: (cents: number) => string;
}

/** Fixed-price marker and the work-specific title under the catalog name (#3655). */
function ServiceHeadingDetails({ service }: {
  service: Pick<WorkOrderDetail['services'][number], 'name' | 'title' | 'pricingType' | 'fixedPriceCents' | 'fixedPriceOverrideCents'>;
}) {
  const { t } = useTranslation('workorders');
  const title = distinctServiceTitle(service.name, service.title);
  return (
    <>
      {isFixedPricePackage(service) && (
        <Chip size="small" variant="outlined" label={t('detail.fixedPricePackage')} sx={{ mr: 0.5 }} />
      )}
      {title && (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }} data-testid="service-title">
          {title}
        </Typography>
      )}
    </>
  );
}

export function WorkOrderServicesSection({
  workOrder,
  workOrderId,
  t,
  expandedServiceIds,
  setExpandedServiceIds,
  accordion,
  activeDragId,
  dndSensors,
  onDragStart,
  onReorderServices,
  onAddService,
  onRemoveService,
  onServiceMechanicChange,
  onServiceAction,
  setServiceDirtyStates,
  updatePending,
  formatPrice,
}: WorkOrderServicesSectionProps) {
  // Stable handler with a no-op bail-out. ServiceDetailContent fires this from
  // an effect whose deps include the callback, so an inline arrow (new identity
  // every render) made that effect re-run every render; combined with the
  // always-new object below it produced an infinite render loop (React #185)
  // during work-order → work-order navigation (issue #420). Memoising the
  // handler and returning the previous state when the flag is unchanged breaks
  // both halves of the loop.
  const handleServiceDirtyChange = useCallback(
    (bookingServiceId: string, dirty: boolean) => {
      setServiceDirtyStates((prev) =>
        prev[bookingServiceId] === dirty ? prev : { ...prev, [bookingServiceId]: dirty },
      );
    },
    [setServiceDirtyStates],
  );

  const canEditBillableHours = useAuthStore((s) => s.hasPermission(EDIT_BILLABLE_HOURS_PERMISSION));

  // Sequential vs parallel calendar layout for this work order's services (#544).
  const updateBooking = useUpdateBooking();
  const [handleVisibility, setHandleVisibility] = usePersistedState<'visible' | 'hidden'>(
    'vs-workorder-services-handles-visible',
    'hidden',
    ['visible', 'hidden'],
  );
  const handlesVisible = handleVisibility === 'visible';

  // Completing a service now completes every unfinished step under it (#1028),
  // so confirm before doing that on the user's behalf.
  const [bulkCompleteTarget, setBulkCompleteTarget] = useState<
    { bookingServiceId: string; serviceName: string; unfinished: number } | null
  >(null);

  const requestServiceComplete = (service: WorkOrderDetail['services'][number]) => {
    const unfinished = service.laborItems.filter((item) => item.status !== 'completed').length;
    if (unfinished === 0) {
      void onServiceAction('complete', service.bookingServiceId);
      return;
    }
    setBulkCompleteTarget({
      bookingServiceId: service.bookingServiceId,
      serviceName: service.name,
      unfinished,
    });
  };

  // The work order's own state can forbid labor-item status commands; the API
  // enforces this, the UI just stops offering the action (#1028).
  const statusActionsLocked = ['quote', 'pending', 'quote_rejected', 'ready_to_invoice', 'invoiced', 'completed', 'cancelled']
    .includes(workOrder.status);

  const bulkCompleteDialog = (
    <ConfirmDialog
      open={bulkCompleteTarget !== null}
      title={t('serviceActions.completeWithUnfinishedTitle')}
      message={t('serviceActions.completeWithUnfinishedMessage', {
        service: bulkCompleteTarget?.serviceName ?? '',
        count: bulkCompleteTarget?.unfinished ?? 0,
      })}
      confirmText={t('serviceActions.completeService')}
      confirmColor="success"
      onConfirm={() => {
        if (bulkCompleteTarget) {
          void onServiceAction('complete', bulkCompleteTarget.bookingServiceId);
        }
        setBulkCompleteTarget(null);
      }}
      onCancel={() => setBulkCompleteTarget(null)}
    />
  );

  if (workOrder.services.length > 0) {
    const completedCount = workOrder.services.filter((service) => service.status === 'completed').length;
    const showHandles = handlesVisible && workOrder.services.length > 1;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {t('serviceActions.servicesProgress', {
            completed: completedCount,
            total: workOrder.services.length,
          })}
        </Typography>

        {workOrder.services.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={<DragIndicatorIcon fontSize="small" />}
            onClick={() => { setHandleVisibility(handlesVisible ? 'hidden' : 'visible'); }}
            sx={{ alignSelf: 'flex-start' }}
          >
            {handlesVisible ? t('detail.hideReorderHandles') : t('detail.showReorderHandles')}
          </Button>
        )}

        {/* Sequential vs parallel calendar layout — only meaningful with 2+ services (#544). */}
        {workOrder.services.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {t('serviceScheduling.label')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={workOrder.serviceScheduling}
              onChange={(_, value) => {
                if (value && value !== workOrder.serviceScheduling) {
                  updateBooking.mutate({ id: workOrderId, data: { serviceScheduling: value } });
                }
              }}
              disabled={updateBooking.isPending}
            >
              <ToggleButton value="sequential">{t('serviceScheduling.sequential')}</ToggleButton>
              <ToggleButton value="parallel">{t('serviceScheduling.parallel')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={onDragStart}
          onDragEnd={(event) => void onReorderServices(event)}
        >
          <SortableContext
            items={workOrder.services.map((service) => service.bookingServiceId)}
            strategy={verticalListSortingStrategy}
          >
            {workOrder.services.map((service, index) => {
              const mechanicName = service.mechanic
                ? [service.mechanic.firstName, service.mechanic.lastName].filter(Boolean).join(' ') || null
                : null;
              const laborHours = sumHours(service.laborItems.map((laborItem) => laborItem.estimatedHours));
              const slotHours = allocatedSlotHours({
                mechanicScheduledStart: workOrder.mechanicScheduledStart,
                mechanicScheduledEnd: workOrder.mechanicScheduledEnd,
              });
              const otherLaborHours = sumHours(
                workOrder.services
                  .filter((entry) => entry.bookingServiceId !== service.bookingServiceId)
                  .flatMap((entry) => entry.laborItems.map((laborItem) => laborItem.estimatedHours)),
              );
              const laborRate = workOrder.laborRateGrossCents
                ?? (workOrder.laborRateCents != null && workOrder.laborRateCents > 0
                  ? netToGrossCents(workOrder.laborRateCents, workOrder.vatPercent ?? DEFAULT_VAT_PERCENT)
                  : null);
              const laborPriceCents = service.pricingType !== 'fixed' && laborRate != null && laborRate > 0
                ? laborCentsFromHours(laborRate, laborHours)
                : 0;

              return (
                <SortableServiceCard
                  key={service.bookingServiceId}
                  id={service.bookingServiceId}
                  showHandle={showHandles}
                  striped={index % 2 === 1}
                  expanded={expandedServiceIds.has(service.id)}
                  onToggle={() => {
                    setExpandedServiceIds((prev) => {
                      if (accordion && !prev.has(service.id)) return new Set([service.id]);
                      const next = new Set(prev);
                      if (next.has(service.id)) next.delete(service.id);
                      else next.add(service.id);
                      return next;
                    });
                  }}
                  title={
                    <>
                      <BuildIcon fontSize="small" color="action" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {service.name}
                        </Typography>
                        <ServiceHeadingDetails service={service} />
                        <WorkOrderDurationTrio
                          plannedHours={service.estimatedDurationHours}
                          billableHours={serviceBillableHours(service)}
                          actual={service}
                        />
                        {service.laborItems.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {laborPriceCents > 0
                              ? t('detail.laborSummaryWithPrice', {
                                  count: service.laborItems.length,
                                  hours: laborHours,
                                  price: formatPrice(laborPriceCents),
                                })
                              : t('detail.laborSummary', {
                                  count: service.laborItems.length,
                                  hours: laborHours,
                                })}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={t(`serviceStatus.${service.status}`)}
                        sx={{
                          backgroundColor: statusColors[service.status],
                          color: 'common.white',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                      {service.mechanic && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <UserAvatar
                            firstName={service.mechanic.firstName ?? ''}
                            lastName={service.mechanic.lastName ?? ''}
                            size={24}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {service.mechanic.firstName} {service.mechanic.lastName}
                          </Typography>
                        </Box>
                      )}
                    </>
                  }
                  actions={
                    <>
                      {service.status === 'pending' && (
                        ['quote', 'pending', 'confirmed', 'quote_rejected'].includes(workOrder.status) ? (
                          <Tooltip title={t('serviceActions.vehicleNotArrived')}>
                            <span>
                              <IconButton
                                disabled
                                size="small"
                                color="primary"
                                aria-label={t('serviceActions.vehicleNotArrived')}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('serviceActions.startService')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => void onServiceAction('start', service.bookingServiceId)}
                              aria-label={t('serviceActions.startService')}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                      {service.status === 'in_progress' && (
                        <>
                          <Tooltip title={t('serviceActions.pauseService')}>
                            <IconButton
                              size="small"
                              onClick={() => void onServiceAction('pause', service.bookingServiceId)}
                              aria-label={t('serviceActions.pauseService')}
                            >
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('serviceActions.completeService')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => requestServiceComplete(service)}
                              aria-label={t('serviceActions.completeService')}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {service.status === 'completed' && !REOPEN_LOCKED_STATUSES.has(workOrder.status) && (
                        // Back under way when the work turns out unfinished (#3403).
                        <Tooltip title={t('serviceActions.reopenService')}>
                          <IconButton
                            size="small"
                            onClick={() => void onServiceAction('reopen', service.bookingServiceId)}
                            aria-label={t('serviceActions.reopenService')}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {service.status === 'paused' && (
                        <>
                          <Tooltip title={t('serviceActions.resumeService')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => void onServiceAction('resume', service.bookingServiceId)}
                              aria-label={t('serviceActions.resumeService')}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('serviceActions.completeService')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => requestServiceComplete(service)}
                              aria-label={t('serviceActions.completeService')}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </>
                  }
                >
                  <ServiceDetailContent
                    detailsBase={detailsBase(workOrder, service.bookingServiceId)}
                    bookingServiceId={service.bookingServiceId}
                    serviceId={service.id}
                    workOrderId={workOrderId}
                    mechanicId={service.assignedMechanicId ?? null}
                    mechanicName={mechanicName}
                    bookingServiceData={service}
                    serviceStatus={service.status}
                    statusActionsLocked={statusActionsLocked}
                    onRemove={(bookingServiceId) => void onRemoveService(bookingServiceId)}
                    onMechanicChange={(bookingServiceId, mechanicId) => void onServiceMechanicChange(bookingServiceId, mechanicId)}
                    onDirtyChange={handleServiceDirtyChange}
                    removing={updatePending}
                    saving={updatePending}
                    laborRateCents={service.pricingType === 'fixed' ? null : laborRate}
                    formatPrice={formatPrice}
                    allocatedSlotHours={slotHours}
                    otherLaborHours={otherLaborHours}
                    vehicleId={workOrder.vehicle.id}
                    vehicleMake={workOrder.vehicle.make}
                    vehicleModel={workOrder.vehicle.model}
                    pricingType={service.pricingType}
                    hoursEditable={canEditBillableHours}
                  />
                </SortableServiceCard>
              );
            })}
          </SortableContext>
          <DragOverlay>
            {activeDragId ? (() => {
              const service = workOrder.services.find((entry) => entry.bookingServiceId === activeDragId);
              if (!service) return null;

              return (
                <Box sx={{ opacity: 0.9 }}>
                  <ExpandableCard
                    expanded={false}
                    expandable={false}
                    title={
                      <>
                        <BuildIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {service.name}
                        </Typography>
                        <ServiceHeadingDetails service={service} />
                        <Chip
                          size="small"
                          label={t(`serviceStatus.${service.status}`)}
                          sx={{
                            backgroundColor: statusColors[service.status],
                            color: 'common.white',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </>
                    }
                  >
                    <span />
                  </ExpandableCard>
                </Box>
              );
            })() : null}
          </DragOverlay>
        </DndContext>
        {displayedUnassignedActualHours(workOrder) > 0 && (
          <Box
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1 }}
            data-testid="unassigned-actual-row"
          >
            <Typography variant="subtitle2">{t('detail.unassignedActual')}</Typography>
            <WorkOrderDurationTrio
              plannedHours={null}
              billableHours={null}
              actual={{
                actualDurationHours: workOrder.unassignedActualDurationHours,
                actualActiveHours: workOrder.unassignedActualActiveHours,
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
          {showHandles && (
            <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', visibility: 'hidden' }}>
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AddServiceRow
              onAdd={onAddService}
              adding={updatePending}
            />
          </Box>
        </Box>
        {bulkCompleteDialog}
      </Box>
    );
  }

  if (workOrder.serviceType) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {workOrder.serviceType.split(', ').filter(Boolean).map((service) => (
            <Chip key={service} label={service} icon={<BuildIcon />} variant="outlined" />
          ))}
        </Box>
        <AddServiceRow
          onAdd={onAddService}
          adding={updatePending}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      <AddServiceRow
        onAdd={onAddService}
        adding={updatePending}
      />
    </Box>
  );
}
